import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import HostLayout from '@/components/mafia/HostLayout';
import { useMafiaAuth } from '@/context/MafiaAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  win_team: 3, mvp_bonus: 5, survive_to_end: 2, correct_check_bonus: 2,
  mafia_kill_bonus: 1, loss_team: 0, inactivity_penalty: -1,
};

const FIELDS = [
  { k: 'win_team', l: 'Победившая команда', hint: 'Очки каждому игроку победившей команды' },
  { k: 'mvp_bonus', l: 'Бонус MVP', hint: 'Дополнительные очки выбранному ведущим MVP' },
  { k: 'survive_to_end', l: 'Дожил до конца', hint: 'Очки за то, что игрок дожил до финального круга' },
  { k: 'correct_check_bonus', l: 'Верная проверка', hint: 'Бонус за каждую верную проверку Дона/Комиссара' },
  { k: 'mafia_kill_bonus', l: 'Удачный выстрел мафии', hint: 'Бонус за каждое удачное ночное убийство' },
  { k: 'loss_team', l: 'Проигравшая команда', hint: 'Очки игрокам проигравшей команды' },
  { k: 'inactivity_penalty', l: 'Штраф за пассив', hint: 'Применяется за каждое событие пассивности' },
];

export default function HostScoringPage() {
  const navigate = useNavigate();
  const { host, loading } = useMafiaAuth();
  const [rules, setRules] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!loading && !host) navigate('/mafia/login', { replace: true }); }, [loading, host, navigate]);
  useEffect(() => {
    if (!host) return;
    fetch(`${API}/api/mafia/auth/scoring`).then(r => r.json()).then(r => setRules({ ...DEFAULTS, ...r })).catch(() => {});
  }, [host]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/mafia/auth/scoring`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rules),
      });
      if (!r.ok) throw new Error();
      toast.success('Scoring rules saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading || !host) return <div className="ms-page flex items-center justify-center ms-text-3">Loading…</div>;

  return (
    <HostLayout title="Очки и правила" subtitle="Настройки алгоритма"
      actions={
        <>
          <button onClick={() => setRules(DEFAULTS)} className="ms-btn ms-btn-secondary ms-btn-sm" data-testid="scoring-reset">
            <RotateCcw size={12} /> Сбросить
          </button>
          <button onClick={save} disabled={saving} className="ms-btn ms-btn-primary" data-testid="scoring-save-btn">
            <Save size={13} /> {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </>
      }
    >
      <div className="max-w-3xl">
        <p className="text-sm ms-text-2 mb-6 leading-relaxed">
          Настройте начисление очков после каждой игры. Изменения применяются к играм, завершённым <em>после</em> сохранения.
        </p>
        <section className="ms-card">
          <div className="ms-divide">
            {FIELDS.map(f => (
              <div key={f.k} className="px-5 py-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm ms-text font-medium">{f.l}</p>
                  <p className="text-xs ms-text-2 mt-0.5">{f.hint}</p>
                </div>
                <input
                  type="number"
                  value={rules[f.k] ?? 0}
                  onChange={(e) => setRules({ ...rules, [f.k]: parseInt(e.target.value || 0, 10) })}
                  data-testid={`rule-${f.k}`}
                  className="ms-input text-right tabular-nums shrink-0"
                  style={{ width: '5.5rem' }}
                />
              </div>
            ))}
          </div>
        </section>
      </div>
    </HostLayout>
  );
}
